<template>
  <div>
    <!--
    Only show for small screen sizes and down, otherwise, keep the
    side nav bar expanded
    -->
    <v-app-bar v-if="$vuetify.breakpoint.smAndDown" app>
      <v-app-bar-nav-icon @click="drawer = true"></v-app-bar-nav-icon>
      <template v-if="$vuetify.breakpoint.smAndUp">
        <v-spacer />

        <v-btn
          v-if="hasTitle"
          text
          class="title grey--text text--darken-2 font-weight-regular ml-6 px-2"
          @click="goToOrg"
        >
          {{ title }}
        </v-btn>
        <div
          v-else
          class="title grey--text text--darken-2 font-weight-regular ml-6"
        >
          Unrulr Dashboard
        </div>
      </template>
      <v-spacer />
    </v-app-bar>
    <v-navigation-drawer
      :permanent="$vuetify.breakpoint.mdAndUp"
      :app="$vuetify.breakpoint.mdAndUp"
      :absolute="$vuetify.breakpoint.smAndDown"
      :temporary="$vuetify.breakpoint.smAndDown"
      v-model="drawer"
      class="left-nav"
    >
      <v-list nav v-if="hasTitle">
        <v-list-item @click="goToOrg">
          <v-list-item-icon>
            <v-icon>mdi-home</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              {{ title }}
            </v-list-item-title>
            <v-list-item-subtitle>
              Dashboard Home
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="orgAdmin" @click="goToSettings">
          <v-list-item-icon>
            <v-icon>mdi-settings</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Settings</v-list-item-title>
            <v-list-item-subtitle>
              Edit name, roles, etc.
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="canSwap" @click="goHome">
          <v-list-item-icon>
            <v-icon>mdi-swap-horizontal</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              Change Organizations
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <!-- For org selection screen -->
      <v-list-item v-else>
        <v-list-item-content>
          <v-list-item-title class="subtitle-1 my-4">
            Unrulr Admin Dashboard
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider />
      <v-list nav>
        <v-list-item href="https://www.unrulr.com">
          <v-list-item-icon>
            <v-icon>mdi-earth</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              Unrulr Website
            </v-list-item-title>
            <v-list-item-subtitle>
              www.unrulr.com
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item href="https://community.unrulr.com">
          <v-list-item-icon>
            <v-icon>mdi-cellphone-iphone</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              Unrulr App
            </v-list-item-title>
            <v-list-item-subtitle>
              community.unrulr.com
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item> </v-list-item>
      </v-list>
      <!--
      Append is broken for the expanding sidebar, so we have to conditionally
      use the slot
      -->
      <template
        v-if="$vuetify.breakpoint.mdAndUp && isAuthenticated"
        v-slot:append
      >
        <v-divider />
        <v-list-item @click="signOut">
          <v-list-item-icon>
            <v-avatar color="grey" size="32">
              <img v-if="hasProfileUrl" v-bind:src="user.pictureUrl" />
              <span v-else class="white--text"> {{ initials }} </span>
            </v-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              Sign out
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-if="$vuetify.breakpoint.smAndDown && isAuthenticated">
        <v-divider />
        <v-list-item @click="signOut">
          <v-list-item-icon>
            <v-avatar color="grey" size="32">
              <img v-if="hasProfileUrl" v-bind:src="user.pictureUrl" />
              <span v-else class="white--text"> {{ initials }} </span>
            </v-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              Sign out
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import store from '@/store';
import { AuthStatus } from '@/store/auth';

export default {
  name: 'AppBar',
  data: () => ({
    drawer: null
  }),
  computed: {
    user: () => store.state.auth.user,
    isAuthenticated: () => store.state.auth.isAuthenticated,
    orgAdmin: () => store.state.org.orgAdmin,
    authStatus: () => store.state.auth.authStatus,
    title: () => store.state.org.basic.name,
    hasTitle: () => !!store.state.org.basic.name,
    hasProfileUrl: () => !!store.state.auth.user.pictureUrl,
    canSwap: () => (store.state.orgs.data || []).length > 1,
    initials: () =>
      store.state.auth.user.name
        .split(' ')
        .map(nameToken => nameToken[0])
        .join('')
  },
  methods: {
    async signOut() {
      await store.dispatch.auth.logout();

      this.$router.push('/login');
    },
    goHome() {
      this.$router.push({ name: 'Organizations' });
    },
    goToOrg() {
      this.$router.push({
        name: 'Organization',
        params: {
          organizationId: `${store.state.org.basic.organizationId}`
        }
      });
    },
    goToSettings() {
      this.$router.push({
        name: 'OrganizationSettings',
        params: {
          organizationId: `${store.state.org.basic.organizationId}`
        }
      });
    }
  },
  watch: {
    authStatus(current, prev) {
      if (current === prev) {
        return;
      }

      if (current === AuthStatus.SUCCESS) {
        store.dispatch.orgs.fetch();
      }
    }
  }
};
</script>
<style scoped></style>
